import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { communicationService, getErrorMessage } from '@/services';
import type { NotificationFilters, TemplateFilters, TicketFilters, NotificationTemplate, SupportTicket } from '@/types';
import type { SupportTicketCreatePayload, TicketCategoryPayload } from '@/services/communications';

// Notifications
export function useNotifications(page: number = 1, filters?: NotificationFilters) {
  return useQuery({
    queryKey: ['notifications', page, filters],
    queryFn: () => communicationService.getNotifications(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useNotification(id: string) {
  return useQuery({
    queryKey: ['notification', id],
    queryFn: () => communicationService.getNotification(id),
    enabled: !!id,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => communicationService.markAsRead(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification', id] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useSendNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      recipient: string;
      channel: string;
      template?: string;
      subject?: string;
      message: string;
    }) => communicationService.sendNotification(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Notification sent successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// Templates
export function useTemplates(page: number = 1, filters?: TemplateFilters) {
  return useQuery({
    queryKey: ['templates', page, filters],
    queryFn: () => communicationService.getTemplates(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: ['template', id],
    queryFn: () => communicationService.getTemplate(id),
    enabled: !!id,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<NotificationTemplate>) => communicationService.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template created successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<NotificationTemplate> }) =>
      communicationService.updateTemplate(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template', variables.id] });
      toast.success('Template updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// Channels
export function useChannels() {
  return useQuery({
    queryKey: ['channels'],
    queryFn: () => communicationService.getChannels(),
  });
}

// Support Tickets
export function useTickets(page: number = 1, filters?: TicketFilters) {
  return useQuery({
    queryKey: ['tickets', page, filters],
    queryFn: () => communicationService.getTickets(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

/** Board #234: the Support Categories configured in the system, for the
 *  ticket create form and the filter bar. */
export function useTicketCategories() {
  return useQuery({
    queryKey: ['ticket-categories'],
    queryFn: () => communicationService.getTicketCategories(),
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Board #262 -- the Support Categories management screen.
//
// Every mutation invalidates ['ticket-categories'], which is also the picker's
// key: rename a category here and the ticket form offers the new name without a
// reload. All five share one toast/error convention with the rest of this file.
// ---------------------------------------------------------------------------

/** The management screen's list: live AND retired rows. */
export function useTicketCategoriesAdmin(enabled: boolean = true) {
  return useQuery({
    queryKey: ['ticket-categories', 'admin'],
    queryFn: () => communicationService.getTicketCategoriesForSettings(),
    enabled,
  });
}

function useCategoryMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<unknown>,
  successMessage: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-categories'] });
      toast.success(successMessage);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useCreateTicketCategory() {
  return useCategoryMutation(
    (data: TicketCategoryPayload) => communicationService.createTicketCategory(data),
    'Support category added',
  );
}

export function useUpdateTicketCategory() {
  return useCategoryMutation(
    ({ id, data }: { id: string; data: Partial<TicketCategoryPayload> }) =>
      communicationService.updateTicketCategory(id, data),
    'Support category updated',
  );
}

/** Retire or restore. Retiring deactivates rather than deletes, so the tickets
 *  already filed under the category stay readable. */
export function useSetTicketCategoryActive() {
  return useCategoryMutation(
    ({ id, isActive }: { id: string; isActive: boolean }) =>
      isActive
        ? communicationService.activateTicketCategory(id)
        : communicationService.deactivateTicketCategory(id),
    'Support category updated',
  );
}

export function useCloneTicketCategory() {
  return useCategoryMutation(
    (id: string) => communicationService.cloneTicketCategory(id),
    'Copied to your categories. You can rename it now.',
  );
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ['ticket', id],
    queryFn: () => communicationService.getTicket(id),
    enabled: !!id,
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SupportTicketCreatePayload) => communicationService.createTicket(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Ticket created successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SupportTicket> }) =>
      communicationService.updateTicket(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', variables.id] });
      toast.success('Ticket updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// Chat
export function useChatMessages(conversationId: string) {
  return useQuery({
    queryKey: ['chat-messages', conversationId],
    queryFn: () => communicationService.getChatMessages(conversationId),
    enabled: !!conversationId,
    refetchInterval: 5000,
  });
}

export function useSendChatMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, message }: { conversationId: string; message: string }) =>
      communicationService.sendChatMessage(conversationId, message),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', variables.conversationId] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
